import React, { useState, useEffect, useMemo } from 'react';
import { Employee } from '../types/employee';
import { employeeApi } from '../services/employeeApi';
import { authApi } from '../services/authApi';
import EmployeeForm, { EmployeeFormData } from '../components/EmployeeForm';
import LeaveModal, { LeaveRequestData } from '../components/LeaveModal';
import ReturnModal, { ReturnRequestData } from '../components/ReturnModal';
import ResignationModal, { ResignationRequestData } from '../components/ResignationModal';
import LeaveHistoryModal from '../components/LeaveHistoryModal';
import PositionManagementModal from '../components/PositionManagementModal';
import SortIcon from '../components/SortIcon';
import { useSort } from '../hooks/useSort';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';

// 직급 순서 정의
const positionOrder = [
  '대표이사',
  '부장',
  '차장',
  '과장',
  '대리',
  'PRINCIPAL', 'Principal',
  'DIRECTOR', 'Director', '디렉터',
  'MANAGER', 'Manager', 'PM', '매니저', '관리자',
  'SENIOR', 'SR', 'Senior', '선임', 'SBA', 'Senior Business Analyst',
  'BUSINESS ANALYST', 'Business Analyst',
  'ASSOCIATE', 'Associate',
  'JUNIOR', 'JR', 'Junior', '사원',
  'RA', '인턴'
];

const EmployeeManagement: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const { userRole, user } = useAuth();
  const { showToast, showConfirm } = useAlert();

  // 부서 필터링 상태 추가
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);

  // 상태 필터링 상태 추가
  const [statusFilter, setStatusFilter] = useState<string>('ACTIVE');

  // 삭제된 직원 보기 상태 추가
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedEmployees, setDeletedEmployees] = useState<Employee[]>([]);

  // 정렬 기능 추가 - position 필드로 초기 정렬 (높은 직급 순서로)
  const currentEmployees = showDeleted ? deletedEmployees : employees;
  
  // position field에 대한 custom sort를 위해 enhanced data 생성
  const employeesWithSortablePosition = useMemo(() => {
    return currentEmployees.map(employee => ({
      ...employee,
      _positionSortValue: (() => {
        // 윤승현은 항상 맨 첫 번째
        if (employee.name === '윤승현') {
          return -1;
        }
        
        // 퇴사자는 맨 마지막
        if (employee.status === 'RESIGNED') {
          return 2000;
        }
        
        const positionUpper = employee.position?.toUpperCase() || '';
        const index = positionOrder.findIndex(pos => 
          positionUpper.includes(pos.toUpperCase())
        );
        return index === -1 ? 999 : index; // 정의되지 않은 직급은 맨 뒤로
      })()
    }));
  }, [currentEmployees]);

  const { sortedData: sortedEmployees, requestSort, getSortDirection } = useSort(
    employeesWithSortablePosition,
    { key: '_positionSortValue', direction: 'asc' } // 초기 정렬: 직급 높은 순서(asc = 낮은 인덱스부터)
  );

  // 필터링된 직원 목록
  const filteredEmployees = selectedDepartment 
    ? sortedEmployees.filter(emp => emp.department === selectedDepartment)
    : sortedEmployees;

  const [isEditing, setIsEditing] = useState(false);
  const [mode, setMode] = useState<'view' | 'edit' | 'new'>('view');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | undefined>();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [isResignationModalOpen, setIsResignationModalOpen] = useState(false);
  const [isLeaveHistoryModalOpen, setIsLeaveHistoryModalOpen] = useState(false);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, [statusFilter]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeeApi.getAllVisible(userRole, undefined, statusFilter);
      setEmployees(data);
    } catch (err) {
      showToast('직원 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedEmployees = async () => {
    try {
      const data = await employeeApi.getDeleted();
      setDeletedEmployees(data);
    } catch (err) {
      showToast('삭제된 직원 목록을 불러오는데 실패했습니다.', 'error');
    }
  };

  const handleDeleteEmployee = async (employeeId: number) => {
    const confirmed = await showConfirm('정말로 이 직원을 삭제하시겠습니까? 삭제된 직원은 나중에 복원할 수 있습니다.');
    if (!confirmed) {
      return;
    }

    try {
      await employeeApi.delete(employeeId);
      await fetchEmployees();
      if (showDeleted) {
        await fetchDeletedEmployees();
      }
      closePanel();
      showToast('직원이 삭제되었습니다.', 'success');

    } catch (err: any) {
      // 일한 기록이 있으면 서버가 이유를 준다(#74) — 그대로 보여 준다
      showToast(err?.message || '직원 삭제에 실패했습니다.', 'error');
    }
  };

  const handleRestoreEmployee = async (employeeId: number) => {
    const confirmed = await showConfirm('이 직원을 복원하시겠습니까?');
    if (!confirmed) {
      return;
    }

    try {
      await employeeApi.restore(employeeId);
      await fetchEmployees();
      await fetchDeletedEmployees();
      closePanel();
      showToast('직원이 복원되었습니다.', 'success');

    } catch (err) {
      showToast('직원 복원에 실패했습니다.', 'error');
    }
  };

  const openPanel = (panelMode: 'view' | 'edit' | 'new', employee?: Employee) => {
    setMode(panelMode);
    setSelectedEmployee(employee);
    setIsEditing(panelMode !== 'view');
    setIsPanelOpen(true);
  };

  const closePanel = () => {
    setIsPanelOpen(false);
    setIsEditing(false);
    setSelectedEmployee(undefined);
  };


  const setEditMode = (editing: boolean) => {
    setIsEditing(editing);
    if (editing && mode === 'view') {
      setMode('edit');
    } else if (!editing && mode === 'edit') {
      setMode('view');
    }
  };

  const saveEmployee = async (formData: EmployeeFormData) => {
    try {
      if (mode === 'new') {
        const { empNo, ...createData } = formData;

        const employeeData: any = {
          ...createData,
          annualSalary: createData.annualSalary ? 
            (typeof createData.annualSalary === 'string' ? 
              Number(createData.annualSalary.replace(/,/g, '')) : 
              createData.annualSalary) : 
            undefined,
          endDate: createData.endDate && createData.endDate.trim() !== '' ? createData.endDate : undefined
        };

        // 학력 정보 처리 (빈 배열도 전송)
        employeeData.education = createData.education && createData.education.length > 0 
          ? createData.education.map(edu => ({
              school: edu.school,
              major: edu.major,
              degree: edu.degree,
              startDate: edu.startDate && edu.startDate.trim() !== '' ? edu.startDate : undefined,
              graduationDate: edu.graduationDate && edu.graduationDate.trim() !== '' ? edu.graduationDate : undefined
            }))
          : [];

        // 경력 정보 처리 (빈 배열도 전송)
        employeeData.experience = createData.experience && createData.experience.length > 0
          ? createData.experience.map(exp => ({
              company: exp.company,
              department: exp.department,
              position: exp.position,
              startDate: exp.startDate && exp.startDate.trim() !== '' ? exp.startDate : undefined,
              endDate: exp.endDate && exp.endDate.trim() !== '' ? exp.endDate : undefined,
              annualSalary: exp.annualSalary && exp.annualSalary.trim() !== '' ? Number(exp.annualSalary.toString().replace(/,/g, '')) : undefined
            }))
          : [];
        
        
        await employeeApi.create(employeeData);

        // 🎯 Cash Flow 자동 재계산 트리거 (직원 생성)
        localStorage.setItem('employee_change_trigger', Date.now().toString());
        window.dispatchEvent(new CustomEvent('employeeChanged'));

      } else if (mode === 'edit' && selectedEmployee) {

        // 수정 시에는 모든 데이터 전송 (상태 변경 포함)
        const updateData: any = {
          ...formData,
          status: formData.status || selectedEmployee.status,
          annualSalary: formData.annualSalary ? 
            (typeof formData.annualSalary === 'string' ? 
              Number(formData.annualSalary.replace(/,/g, '')) : 
              formData.annualSalary) : 
            undefined,
          // 빈 날짜 문자열 처리
          endDate: formData.endDate && formData.endDate.trim() !== '' ? formData.endDate : undefined,
          // SSN 빈 문자열 처리
          ssn: formData.ssn && formData.ssn.trim() !== '' ? formData.ssn : undefined,
          // 계좌번호 빈 문자열 처리
          bankAccount: formData.bankAccount && formData.bankAccount.trim() !== '' ? formData.bankAccount : undefined,
          // 은행명 빈 문자열 처리
          bankName: formData.bankName && formData.bankName.trim() !== '' ? formData.bankName : undefined
        };

        // 학력 정보 처리 (빈 배열도 전송)
        updateData.education = formData.education && formData.education.length > 0
          ? formData.education.map(edu => ({
              school: edu.school,
              major: edu.major,
              degree: edu.degree,
              startDate: edu.startDate && edu.startDate.trim() !== '' ? edu.startDate : undefined,
              graduationDate: edu.graduationDate && edu.graduationDate.trim() !== '' ? edu.graduationDate : undefined
            }))
          : [];

        // 경력 정보 처리 (빈 배열도 전송)
        updateData.experience = formData.experience && formData.experience.length > 0
          ? formData.experience.map(exp => ({
              company: exp.company,
              department: exp.department,
              position: exp.position,
              startDate: exp.startDate && typeof exp.startDate === 'string' && exp.startDate.trim() !== '' ? exp.startDate : undefined,
              endDate: exp.endDate && typeof exp.endDate === 'string' && exp.endDate.trim() !== '' ? exp.endDate : undefined,
              annualSalary: exp.annualSalary ? 
                (typeof exp.annualSalary === 'string' ? 
                  Number(exp.annualSalary.replace(/,/g, '')) : 
                  (typeof exp.annualSalary === 'number' ? exp.annualSalary : undefined)) : 
                undefined
            }))
          : [];

        await employeeApi.update(selectedEmployee.id, updateData);
      }
      closePanel();
      await fetchEmployees(); // Refresh the list
    } catch (err) {
      // 에러 메시지 분석 및 사용자 친화적 메시지 생성
      console.error('저장 오류:', err); // 전체 오류 로그
      let errorMessage = '직원 정보 저장에 실패했습니다.';

      if (err instanceof Error) {
        console.error('에러 메시지:', err.message); // 에러 메시지만 로그
        if (err.message.includes('column employee.education does not exist')) {
          errorMessage = '데이터베이스 스키마 오류: education 컬럼이 존재하지 않습니다. 개발자에게 문의하세요.';
        } else if (err.message.includes('column employee.experience does not exist')) {
          errorMessage = '데이터베이스 스키마 오류: experience 컬럼이 존재하지 않습니다. 개발자에게 문의하세요.';
        } else if (err.message.includes('property age should not exist')) {
          errorMessage = '데이터 오류: 나이 필드가 포함되어 있습니다. 페이지를 새로고침 후 다시 시도하세요.';
        } else if (err.message.includes('Bad Request')) {
          errorMessage = `입력 데이터 오류: ${err.message}`;
        } else {
          errorMessage = `오류: ${err.message}`;
        }
      }

      showToast(errorMessage, 'error');
    }
  };

  const handleLeaveRequest = async (data: LeaveRequestData) => {
    try {
      if (selectedEmployee) {
        // 새로운 휴직 신청 API 호출
        await employeeApi.processLeaveRequest(selectedEmployee.id, {
          startDate: data.startDate,
          returnDate: data.returnDate,
          reason: data.reason,
          payType: data.payType,
          memo: data.memo
        });
        await fetchEmployees();
        showToast('휴직 신청이 성공적으로 처리되었습니다.', 'success');
      }
    } catch (err) {
      showToast('휴직 신청에 실패했습니다.', 'error');
    }
    setIsLeaveModalOpen(false);
    closePanel();
  };

  const handleReturnRequest = async (data: ReturnRequestData) => {
    try {
      if (selectedEmployee) {
        // 새로운 복직 처리 API 호출 (메모 포함)
        await employeeApi.processReturnFromLeave(selectedEmployee.id, data.memo);
        await fetchEmployees();
        showToast('복직이 성공적으로 처리되었습니다.', 'success');
      }
    } catch (err) {
      showToast('복직 처리에 실패했습니다.', 'error');
    }
    setIsReturnModalOpen(false);
    closePanel();
  };

  const handleResignationRequest = async (data: ResignationRequestData) => {
    try {
      if (selectedEmployee) {
        // 새로운 퇴사 신청 API 호출
        await employeeApi.processResignationRequest(selectedEmployee.id, {
          resignDate: data.resignDate,
          reason: data.reason,
          leaveAccrued: data.leaveAccrued,
          leaveUsed: data.leaveUsed,
          leaveRemaining: data.leaveRemaining,
          leaveAllowance: data.leaveAllowance,
          severancePay: data.severancePay,
          memo: data.memo
        });
        await fetchEmployees();
        showToast('퇴사 신청이 성공적으로 처리되었습니다.', 'success');
      }
    } catch (err) {
      showToast('퇴사 신청에 실패했습니다.', 'error');
    }
    setIsResignationModalOpen(false);
    closePanel();
  };

  const handleCancelResignation = async (employeeId: number) => {
    const confirmed = await showConfirm('퇴사를 취소하시겠습니까? 재직 상태로 변경되고 퇴사일이 제거됩니다.');
    if (!confirmed) {
      return;
    }

    try {
      await employeeApi.cancelResignation(employeeId);
      await fetchEmployees();
      closePanel();
      showToast('퇴사가 취소되었습니다. 재직 상태로 변경되었습니다.', 'success');
    } catch (err) {
      showToast('퇴사 취소에 실패했습니다.', 'error');
    }
  };

  const handleResetPassword = async (employeeId: number, employeeName: string) => {
    const confirmed = await showConfirm(`${employeeName}님의 비밀번호를 사번으로 초기화하시겠습니까?`);
    if (!confirmed) {
      return;
    }

    try {
      const result = await authApi.resetPassword(employeeId);
      showToast(result.message, 'success');
    } catch (err: any) {
      showToast(err.message || '비밀번호 초기화에 실패했습니다.', 'error');
    }
  };

  const getStatusStyle = (status: string) => {
    const styles = {
      'ACTIVE': 'bg-green-100 text-green-800',
      'ON_LEAVE': 'bg-yellow-100 text-yellow-800',
      'RESIGNED': 'bg-slate-200 text-slate-600',
      'INACTIVE': 'bg-red-100 text-red-800',
      'VIRTUAL': 'bg-purple-100 text-purple-800'
    };
    return styles[status as keyof typeof styles] || 'bg-slate-100 text-slate-800';
  };

  const getStatusText = (status: string) => {
    const statusText = {
      'ACTIVE': '재직',
      'ON_LEAVE': '휴직',
      'RESIGNED': '퇴사',
      'INACTIVE': '비활성',
      'VIRTUAL': '가상'
    };
    return statusText[status as keyof typeof statusText] || status;
  };

  const getDepartmentStats = () => {
    const departmentCounts: { [key: string]: number } = {};

    // 현재 상태 필터에 맞는 직원만 카운트
    sortedEmployees.forEach(employee => {
      const dept = employee.department || '미정';
      departmentCounts[dept] = (departmentCounts[dept] || 0) + 1;
    });

    return Object.entries(departmentCounts)
      .map(([department, count]) => ({
        department,
        count,
        isSelected: selectedDepartment === department
      }))
      .sort((a, b) => b.count - a.count); // 직원수 많은 순으로 정렬
  };

  const handleDepartmentFilter = (department: string) => {
    if (selectedDepartment === department) {
      // 이미 선택된 부서를 클릭하면 필터 해제
      setSelectedDepartment(null);
    } else {
      // 새로운 부서 선택
      setSelectedDepartment(department);
    }
  };

  const toggleDeletedView = async () => {
    if (!showDeleted) {
      // 삭제된 직원들을 보여주기 전에 데이터 로드
      await fetchDeletedEmployees();
    }
    setShowDeleted(!showDeleted);
    setSelectedDepartment(null); // 필터 초기화
  };

  return (
    <>
      {/* 메인 영역 */}
      <main className="flex-1 overflow-y-auto p-6 lg:p-8">
        <header className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">인력관리</h1>
            <div className="flex items-center gap-4 mt-2">
              <p className="text-slate-500">
                {showDeleted
                  ? selectedDepartment 
                    ? `${selectedDepartment} 부서의 삭제된 직원을 관리합니다.` 
                    : '삭제된 직원들을 관리합니다.'
                  : selectedDepartment 
                    ? `${selectedDepartment} 부서의 직원을 관리합니다.` 
                    : '전체 직원의 정보를 관리합니다.'
                }
              </p>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {selectedDepartment
                    ? `${filteredEmployees.length}명`
                    : `총 ${sortedEmployees.length}명`}
                  {selectedDepartment && (
                    <button
                      onClick={() => setSelectedDepartment(null)}
                      className="ml-1 hover:bg-indigo-200 rounded-full p-0.5"
                      title="필터 해제"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </span>
                {getDepartmentStats().map((stat, index) => (
                  <button
                    key={index}
                    onClick={() => handleDepartmentFilter(stat.department)}
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors cursor-pointer hover:bg-slate-200 ${
                      stat.isSelected 
                        ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-200' 
                        : 'bg-slate-100 text-slate-700'
                    }`}
                    title={`${stat.department} 부서만 보기`}
                  >
                    {stat.department} {stat.count}명
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsPositionModalOpen(true)}
              className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
              직급 관리
            </button>
            <button
              onClick={() => setIsLeaveHistoryModalOpen(true)}
              className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              히스토리
            </button>
            <button
              onClick={toggleDeletedView}
              className={`px-4 py-2 text-sm font-semibold rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center ${
                showDeleted
                  ? 'text-white bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  : 'text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 focus:ring-slate-500'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                {showDeleted ? (
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                ) : (
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                )}
              </svg>
              {showDeleted ? '활성 보기' : '삭제 보기'}
            </button>
            {!showDeleted && (
              <>
                <button
                  onClick={() => openPanel('new')}
                  className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 11a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1v-1z" />
                  </svg>
                  추가
                </button>
              </>
            )}
          </div>
        </header>

        {/* 상태 필터 버튼 그룹 */}
        {!showDeleted && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">상태:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('ACTIVE')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'ACTIVE'
                    ? 'bg-green-100 text-green-800 ring-2 ring-green-200'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                재직
              </button>
              <button
                onClick={() => setStatusFilter('RESIGNED')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'RESIGNED'
                    ? 'bg-slate-200 text-slate-800 ring-2 ring-slate-300'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                퇴사
              </button>
              <button
                onClick={() => setStatusFilter('ON_LEAVE')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'ON_LEAVE'
                    ? 'bg-yellow-100 text-yellow-800 ring-2 ring-yellow-200'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                휴직
              </button>
              <button
                onClick={() => setStatusFilter('VIRTUAL')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'VIRTUAL'
                    ? 'bg-purple-100 text-purple-800 ring-2 ring-purple-200'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                가상
              </button>
              <button
                onClick={() => setStatusFilter('ALL')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'ALL'
                    ? 'bg-indigo-100 text-indigo-800 ring-2 ring-indigo-200'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                전체
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          {loading ? (
            <div className="p-8 text-center">
              <p className="text-slate-500">직원 목록을 불러오는 중...</p>
            </div>
          ) : currentEmployees.length === 0 ? (
            <div className="p-8 text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {showDeleted ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  )}
                </svg>
              </div>
              <p className="text-slate-500 text-lg font-medium mb-2">
                {showDeleted ? '삭제된 직원이 없습니다' : '등록된 직원이 없습니다'}
              </p>
              <p className="text-slate-400 text-sm">
                {showDeleted ? '삭제된 직원들이 여기에 표시됩니다.' : '새 직원을 추가해서 시작해보세요.'}
              </p>
              <div className="mt-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600">
                  총 0명
                </span>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 font-medium text-center w-16">순번</th>
                    <th className="px-6 py-3 font-medium text-left w-min">사진</th>
                    <th className="px-6 py-3 font-medium text-left">
                      <button
                        className="group flex items-center space-x-1 hover:text-slate-700 transition-colors"
                        onClick={() => requestSort('name')}
                      >
                        <span>이름</span>
                        <SortIcon direction={getSortDirection('name')} />
                      </button>
                    </th>
                    <th className="px-6 py-3 font-medium text-left">
                      <button
                        className="group flex items-center space-x-1 hover:text-slate-700 transition-colors"
                        onClick={() => requestSort('empNo')}
                      >
                        <span>사번</span>
                        <SortIcon direction={getSortDirection('empNo')} />
                      </button>
                    </th>
                    <th className="px-6 py-3 font-medium text-left">
                      <button
                        className="group flex items-center space-x-1 hover:text-slate-700 transition-colors"
                        onClick={() => requestSort('_positionSortValue')}
                      >
                        <span>직급</span>
                        <SortIcon direction={getSortDirection('_positionSortValue')} />
                      </button>
                    </th>
                    <th className="px-6 py-3 font-medium text-left">
                      <button
                        className="group flex items-center space-x-1 hover:text-slate-700 transition-colors"
                        onClick={() => requestSort('department')}
                      >
                        <span>부서</span>
                        <SortIcon direction={getSortDirection('department')} />
                      </button>
                    </th>
                    <th className="px-6 py-3 font-medium text-left">
                      <button
                        className="group flex items-center space-x-1 hover:text-slate-700 transition-colors"
                        onClick={() => requestSort('status')}
                      >
                        <span>상태</span>
                        <SortIcon direction={getSortDirection('status')} />
                      </button>
                    </th>
                    <th className="px-6 py-3 font-medium text-left">
                      <button
                        className="group flex items-center space-x-1 hover:text-slate-700 transition-colors"
                        onClick={() => requestSort('joinDate')}
                      >
                        <span>입사일</span>
                        <SortIcon direction={getSortDirection('joinDate')} />
                      </button>
                    </th>
                    <th className="px-6 py-3 font-medium text-center">권한</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  {filteredEmployees.map((employee, index) => (
                    <tr
                      key={employee.id}
                      className={`border-t border-slate-200 hover:bg-slate-50 cursor-pointer ${
                        showDeleted ? 'bg-red-50 opacity-75' : ''
                      }`}
                      onClick={() => openPanel('view', employee)}
                    >
                      <td className="px-4 py-3 text-center text-slate-500 font-medium">{index + 1}</td>
                      <td className="px-6 py-3">
                        <img
                          className="h-10 w-10 rounded-full object-cover border border-slate-200"
                          src={employee.profileImageUrl 
                            ? employee.profileImageUrl 
                            : `https://placehold.co/40x40/E2E8F0/4A5568?text=${employee.name.charAt(0)}`
                          }
                          alt={employee.name}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://placehold.co/40x40/E2E8F0/4A5568?text=${employee.name.charAt(0)}`;
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 font-semibold text-slate-900">{employee.name}</td>
                      <td className="px-6 py-4">{employee.empNo}</td>
                      <td className="px-6 py-4">{employee.position}</td>
                      <td className="px-6 py-4">{employee.department}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(employee.status)}`}>
                          {getStatusText(employee.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {new Date(employee.joinDate).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          employee.role === 'DEVELOPER' ? 'bg-purple-100 text-purple-800' :
                          employee.role === 'ADMIN' ? 'bg-red-100 text-red-800' :
                          employee.role === 'PROJECT_MANAGER' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {employee.role === 'DEVELOPER' ? '개발자' :
                           employee.role === 'ADMIN' ? '관리자' :
                           employee.role === 'PROJECT_MANAGER' ? 'PM' : '직원'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
            </div>
          )}
        </div>
      </main>

      {/* 상세 정보 패널 */}
      {isPanelOpen && (
        <div className="fixed inset-0 z-30">
          <div
            className="absolute inset-0 bg-black bg-opacity-20"
            onClick={closePanel}
          ></div>
          <div className="absolute top-0 right-0 h-full w-full max-w-3xl bg-white shadow-2xl flex flex-col transform transition-transform duration-300">
            <header className="h-16 flex-shrink-0 border-b border-slate-200 flex items-center justify-between px-6">
              <h2 className="text-lg font-bold">
                {mode === 'new' ? '새 직원 추가' : '직원 정보'}
              </h2>
              <button
                onClick={closePanel}
                className="p-2 rounded-full text-slate-500 hover:bg-slate-100 text-2xl"
              >
                &times;
              </button>
            </header>
            <div className="flex-1 overflow-y-auto p-6">
              <EmployeeForm
                employee={selectedEmployee}
                isEditing={isEditing}
                mode={mode}
                onSubmit={saveEmployee}
                onCancel={closePanel}
              />
            </div>
            {/* 뷰 모드일 때만 footer 표시 (가상 직원은 버튼 숨김) */}
            {mode === 'view' && selectedEmployee?.status !== 'VIRTUAL' && (
              <footer className="h-16 flex-shrink-0 bg-slate-50 border-t border-slate-200 flex items-center justify-end px-6 space-x-2">
                {showDeleted ? (
                  // 삭제된 직원 보기 모드일 때 복원 버튼
                  <button
                    type="button"
                    onClick={() => selectedEmployee && handleRestoreEmployee(selectedEmployee.id)}
                    className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    복원
                  </button>
                ) : (
                  // 일반 모드일 때 기존 버튼들
                  <>
                    {selectedEmployee?.status === 'RESIGNED' ? (
                      <button
                        type="button"
                        onClick={() => selectedEmployee && handleCancelResignation(selectedEmployee.id)}
                        className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        퇴사 취소
                      </button>
                    ) : selectedEmployee?.status === 'ON_LEAVE' ? (
                      <button
                        type="button"
                        onClick={() => setIsReturnModalOpen(true)}
                        className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                      >
                        복직
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsLeaveModalOpen(true)}
                        className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                      >
                        휴직
                      </button>
                    )}
                    {selectedEmployee?.status !== 'RESIGNED' && (
                      <button
                        type="button"
                        onClick={() => setIsResignationModalOpen(true)}
                        className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                      >
                        퇴사
                      </button>
                    )}
                    {(userRole === 'DEVELOPER' || userRole === 'ADMIN') && selectedEmployee && (
                      <button
                        type="button"
                        onClick={() => handleResetPassword(selectedEmployee.id, selectedEmployee.name)}
                        className="px-4 py-2 text-sm font-semibold text-white bg-orange-600 rounded-lg hover:bg-orange-700"
                        title="비밀번호를 사번으로 초기화"
                      >
                        비밀번호 초기화
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => selectedEmployee && handleDeleteEmployee(selectedEmployee.id)}
                      className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      삭제
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditMode(true)}
                      className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    >
                      수정
                    </button>
                  </>
                )}
              </footer>
            )}
          </div>
        </div>
      )}

      {/* 모달들 */}
      <LeaveModal
        isOpen={isLeaveModalOpen}
        onClose={() => setIsLeaveModalOpen(false)}
        employee={selectedEmployee}
        onSubmit={handleLeaveRequest}
      />

      <ReturnModal
        isOpen={isReturnModalOpen}
        onClose={() => setIsReturnModalOpen(false)}
        employee={selectedEmployee}
        onSubmit={handleReturnRequest}
      />

      <ResignationModal
        isOpen={isResignationModalOpen}
        onClose={() => setIsResignationModalOpen(false)}
        employee={selectedEmployee}
        onSubmit={handleResignationRequest}
      />

      <LeaveHistoryModal
        isOpen={isLeaveHistoryModalOpen}
        onClose={() => setIsLeaveHistoryModalOpen(false)}
      />

      <PositionManagementModal
        isOpen={isPositionModalOpen}
        onClose={() => setIsPositionModalOpen(false)}
        onPositionsUpdated={fetchEmployees}
      />

    </>
  );
};

export default EmployeeManagement;